
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { FavoriteButton } from "@/components/airport/FavoriteButton";
import { Loader2, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslation } from "@/hooks/useTranslation";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

interface Airport {
  id: number;
  name: string;
  iata_code: string | null;
  location: string;
}

interface FavoriteAirport extends Airport {
  airport_favorites: {
    created_at: string;
  };
}

interface ProfileFavoritesProps {
  userId?: string;
}

export function ProfileFavorites({ userId }: ProfileFavoritesProps) {
  const [favorites, setFavorites] = useState<FavoriteAirport[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useIsMobile();
  const { t } = useTranslation();

  useEffect(() => {
    const fetchFavorites = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        // Only show favorites if it's the current user's profile
        if (!user || (userId && userId !== user.id)) {
          setIsLoading(false);
          return;
        }

        const { data, error } = await supabase
          .from('airports')
          .select(`
            id,
            name,
            iata_code,
            location,
            airport_favorites!inner (
              created_at
            )
          `)
          .eq('airport_favorites.user_id', user.id)
          .order('created_at', { foreignTable: 'airport_favorites', ascending: false });

        if (error) {
          console.error('Error fetching favorites:', error);
          return;
        }

        const transformedData = data?.map(item => ({
          ...item,
          airport_favorites: (item.airport_favorites as any)[0]
        }));

        setFavorites(transformedData || []);
      } catch (error) {
        console.error('Error in fetchFavorites:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFavorites();
  }, [userId]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-24">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  // Don't show anything if not viewing own profile or if no favorites
  if (favorites.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        {t('no_favorite_airports')}
      </div>
    );
  }

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className="w-full"
    >
      <CollapsibleTrigger className="flex items-center gap-2 w-full py-4">
        <h2 className={`${isMobile ? 'text-xl' : 'text-2xl'} font-semibold flex items-center gap-2`}>
          {t('favorite_airports')} ({favorites.length})
          <ChevronDown
            className={`h-4 w-4 transition-transform ${
              isOpen ? "transform rotate-180" : ""
            }`}
          />
        </h2>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="divide-y border rounded-lg mt-2">
          {favorites.map((airport) => (
            <div 
              key={airport.id} 
              className={`flex items-center justify-between ${isMobile ? 'p-3' : 'p-4'}`}
            >
              <div className="flex-1 min-w-0 pr-2">
                <Link 
                  to={`/airport/${airport.id}`}
                  className="font-medium hover:underline block truncate"
                >
                  {t(airport.name) || airport.name}
                </Link>
                <div className="text-sm text-muted-foreground truncate">
                  {airport.iata_code && `${airport.iata_code} • `}{t(airport.location) || airport.location}
                </div>
              </div>
              <div className="flex-shrink-0">
                <FavoriteButton 
                  airportId={airport.id} 
                  initialFavorited={true}
                />
              </div>
            </div>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
